import {
  formatHandoffBriefPlainText,
  type MarketplaceOrderHandoffDefaults,
  type MarketplaceOrderHandoffSnapshot,
} from "@nexus/contracts";
import { Button, DropdownMenuItem } from "@nexus/ui";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { orderHandoffDefaultsQueryOptions } from "@/lib/queries";

import type { MarketplaceFulfillmentViewer } from "../../../lib/fulfillment-badge-variant";
import { vendorBriefIsRecorded } from "../../../lib/vendor-brief-display";
import { CompareDefaultsDialog } from "./CompareDefaultsDialog";
import { VendorEmailDialog } from "./VendorEmailDialog";

export function canShowVendorBriefActions(
  snapshot: MarketplaceOrderHandoffSnapshot | null | undefined,
  viewer: MarketplaceFulfillmentViewer = "client",
): boolean {
  return viewer !== "partner" && vendorBriefIsRecorded(snapshot);
}

interface VendorBriefActionsProps {
  snapshot: MarketplaceOrderHandoffSnapshot | null | undefined;
  orderId: string;
  tenantId: string;
  tenantVendorId?: string | null;
  sentByUserName?: string | null;
  orgDefaults?: MarketplaceOrderHandoffDefaults | null;
  viewer?: MarketplaceFulfillmentViewer;
  /** Menu items stay in the marketplace overflow. Hub renders the same actions as buttons. */
  presentation?: "menu" | "buttons";
  children: (menuItems: ReactNode) => ReactNode;
}

export function VendorBriefActions({
  snapshot,
  orderId,
  tenantId,
  tenantVendorId,
  sentByUserName,
  orgDefaults,
  viewer = "client",
  presentation = "menu",
  children,
}: VendorBriefActionsProps) {
  const { t } = useTranslation();
  const { copy } = useCopyToClipboard({
    onCopy: () =>
      toast.success(t("marketplace.orders.list.brief.copied", "Copied")),
  });
  const [emailOpen, setEmailOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const defaultsQuery = useQuery({
    ...orderHandoffDefaultsQueryOptions(tenantId, tenantVendorId),
    enabled:
      canShowVendorBriefActions(snapshot, viewer) &&
      orgDefaults === undefined &&
      Boolean(tenantId && tenantVendorId),
  });
  const resolvedDefaults = orgDefaults ?? defaultsQuery.data?.defaults ?? null;

  if (!snapshot || !canShowVendorBriefActions(snapshot, viewer)) {
    return children(null);
  }

  const copyLabel = t("marketplace.orders.list.brief.copyAll", "Copy full brief");
  const emailLabel = t(
    "marketplace.orders.list.brief.openEmail",
    "Open partner email",
  );
  const compareLabel = t(
    "marketplace.orders.list.brief.compareShort",
    "Compare defaults",
  );

  const copyBrief = () => void copy(formatHandoffBriefPlainText(snapshot));
  const actions =
    presentation === "buttons" ? (
      <>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="copy"
          onClick={copyBrief}
        >
          {copyLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="mail"
          onClick={() => setEmailOpen(true)}
        >
          {emailLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconStart="columns"
          onClick={() => setCompareOpen(true)}
        >
          {compareLabel}
        </Button>
      </>
    ) : (
      <>
        <DropdownMenuItem icon="copy" className="text-xs" onSelect={copyBrief}>
          {copyLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="mail"
          className="text-xs"
          onSelect={() => setEmailOpen(true)}
        >
          {emailLabel}
        </DropdownMenuItem>
        <DropdownMenuItem
          icon="columns"
          className="text-xs"
          onSelect={() => setCompareOpen(true)}
        >
          {compareLabel}
        </DropdownMenuItem>
      </>
    );

  return (
    <>
      {children(actions)}
      <VendorEmailDialog
        snapshot={snapshot}
        orderId={orderId}
        sentByUserName={sentByUserName}
        open={emailOpen}
        onOpenChange={setEmailOpen}
      />
      <CompareDefaultsDialog
        snapshot={snapshot}
        orgDefaults={resolvedDefaults}
        open={compareOpen}
        onOpenChange={setCompareOpen}
      />
    </>
  );
}
